/**
 * FSS Wishlist System v2
 * Stores product handle alongside ID so drawer can fetch correctly
 */

const FSS_WISHLIST_KEY = 'fss_wishlist';
const FSS_HANDLES_KEY = 'fss_wishlist_handles';

class FSSWishlist {
  constructor() {
    this.items = this.load();
    this.listeners = [];
    this.init();
  }
  load() {
    try { return JSON.parse(localStorage.getItem(FSS_WISHLIST_KEY) || '[]'); }
    catch { return []; }
  }
  save() {
    try { localStorage.setItem(FSS_WISHLIST_KEY, JSON.stringify(this.items)); }
    catch {}
    this.notify();
  }
  has(id) { return this.items.includes(String(id)); }
  add(id) {
    const s = String(id);
    if (!this.items.includes(s)) { this.items.push(s); this.save(); }
  }
  remove(id) {
    this.items = this.items.filter(i => i !== String(id));
    // Clean up handle store too
    try {
      const h = JSON.parse(localStorage.getItem(FSS_HANDLES_KEY) || '{}');
      delete h[String(id)];
      localStorage.setItem(FSS_HANDLES_KEY, JSON.stringify(h));
    } catch {}
    this.save();
  }
  toggle(id) {
    if (this.has(id)) { this.remove(id); return false; }
    this.add(id); return true;
  }
  count() { return this.items.length; }
  notify() {
    this.listeners.forEach(fn => fn(this.items));
    document.dispatchEvent(new CustomEvent('fss:wishlist:updated', {
      detail: { items: this.items, count: this.items.length }
    }));
  }
  init() { this.notify(); }
}

window.FSSWishlist = window.FSSWishlist || new FSSWishlist();

// Save handle helper
function fssSaveHandle(productId, handle) {
  if (!handle) return;
  try {
    const h = JSON.parse(localStorage.getItem(FSS_HANDLES_KEY) || '{}');
    h[String(productId)] = handle;
    localStorage.setItem(FSS_HANDLES_KEY, JSON.stringify(h));
  } catch {}
}

function fssGetHandle(productId) {
  try {
    const h = JSON.parse(localStorage.getItem(FSS_HANDLES_KEY) || '{}');
    return h[String(productId)] || null;
  } catch { return null; }
}

// -- Heart Button --

class FSSWishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    // Save handle immediately on page load (even before clicking)
    if (this.productHandle) fssSaveHandle(this.productId, this.productHandle);
    this.render();
    this._update = () => this.render();
    document.addEventListener('fss:wishlist:updated', this._update);
  }
  disconnectedCallback() {
    document.removeEventListener('fss:wishlist:updated', this._update);
  }
  render() {
    const active = window.FSSWishlist.has(this.productId);
    this.setAttribute('aria-pressed', active);
    this.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    this.classList.toggle('fss-wishlist-btn--active', active);
  }
  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.productHandle) fssSaveHandle(this.productId, this.productHandle);
    const added = window.FSSWishlist.toggle(this.productId);
    this.render();
    FSSWishlistToast.show(added ? 'Added to wishlist' : 'Removed from wishlist');
  }
}

if (!customElements.get('fss-wishlist-button')) {
  customElements.define('fss-wishlist-button', FSSWishlistButton);
}

// -- Count Badge --

class FSSWishlistCount extends HTMLElement {
  connectedCallback() {
    this._update = (e) => this.update(e.detail.count);
    document.addEventListener('fss:wishlist:updated', this._update);
    this.update(window.FSSWishlist.count());
  }
  disconnectedCallback() {
    document.removeEventListener('fss:wishlist:updated', this._update);
  }
  update(count) {
    this.textContent = count > 0 ? count : '';
    this.style.display = count > 0 ? 'flex' : 'none';
    this.closest('.fss-wishlist-header-btn')?.classList.toggle('fss-wishlist-header-btn--has-items', count > 0);
  }
}

if (!customElements.get('fss-wishlist-count')) {
  customElements.define('fss-wishlist-count', FSSWishlistCount);
}

// -- Drawer --

class FSSWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this._update = () => { if (this.dialog?.open) this.renderItems(); };
    document.addEventListener('fss:wishlist:updated', this._update);
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.dialog?.open) this.close(); });
  }
  disconnectedCallback() {
    document.removeEventListener('fss:wishlist:updated', this._update);
  }
  open() {
    this.renderItems();
    this.dialog?.showModal();
    document.body.style.overflow = 'hidden';
  }
  close() {
    this.dialog?.close();
    document.body.style.overflow = '';
  }

  async fetchByHandle(handle) {
    const r = await fetch('/products/' + handle + '.js');
    if (!r.ok) throw new Error('not found');
    return r.json();
  }

  async fetchById(id) {
    // Try stored handle first
    const handle = fssGetHandle(id);
    if (handle) {
      try { return await this.fetchByHandle(handle); } catch {}
    }
    // Fallback: search API
    try {
      const r = await fetch('/search/suggest.json?q=' + id + '&resources[type]=product&resources[limit]=10');
      if (!r.ok) return null;
      const data = await r.json();
      const products = data?.resources?.results?.products || [];
      const match = products.find(p => String(p.id) === String(id));
      if (match && match.handle) {
        fssSaveHandle(id, match.handle);
        return await this.fetchByHandle(match.handle);
      }
    } catch {}
    return null;
  }

  async renderItems() {
    const ids = window.FSSWishlist.items;
    const grid = this.querySelector('.fss-wishlist-drawer__grid');
    const empty = this.querySelector('.fss-wishlist-drawer__empty');
    const countEl = this.querySelector('.fss-wishlist-drawer__count');

    if (countEl) countEl.textContent = ids.length > 0
      ? ids.length + ' item' + (ids.length === 1 ? '' : 's') : '';

    if (!grid) return;

    if (ids.length === 0) {
      grid.innerHTML = '';
      if (empty) empty.style.display = '';
      return;
    }

    if (empty) empty.style.display = 'none';
    grid.innerHTML = '<div class="fss-wishlist-drawer__loading"><span></span><span></span><span></span></div>';

    try {
      const products = (await Promise.all(ids.map(id => this.fetchById(id)))).filter(Boolean);

      if (products.length === 0) {
        grid.innerHTML = '<p style="padding:24px;text-align:center;opacity:0.5;font-size:0.82rem;">Could not load wishlist items. Try re-hearting the products.</p>';
        return;
      }

      grid.innerHTML = products.map(p => this.productCard(p)).join('');

      grid.querySelectorAll('[data-wishlist-remove]').forEach(btn => {
        btn.addEventListener('click', () => window.FSSWishlist.remove(btn.dataset.wishlistRemove));
      });

      grid.querySelectorAll('[data-wishlist-atc]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.wishlistAtc;
          btn.disabled = true; btn.textContent = '...';
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
            btn.textContent = 'Added';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setTimeout(() => { btn.textContent = 'Add to bag'; btn.disabled = false; }, 1800);
          } catch { btn.textContent = 'Add to bag'; btn.disabled = false; }
        });
      });

    } catch {
      grid.innerHTML = '<p style="padding:24px;text-align:center;opacity:0.5;font-size:0.82rem;">Unable to load wishlist.</p>';
    }
  }

  productCard(p) {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const fmt = v => (v / 100).toLocaleString('en-GB', { style: 'currency', currency });
    const price = p.price ? fmt(p.price) : '';
    const compare = p.compare_at_price && p.compare_at_price > p.price ? fmt(p.compare_at_price) : '';
    const img = p.featured_image || (p.images && p.images[0]);
    const variantId = p.variants && p.variants[0] && p.variants[0].id;
    const available = p.available !== false;
    const url = '/products/' + p.handle;

    return '<div class="fss-wishlist-card" data-product-id="' + p.id + '">'
      + '<a href="' + url + '" class="fss-wishlist-card__image-wrap" tabindex="-1">'
      + (img ? '<img src="' + img + '" alt="' + p.title + '" loading="lazy" width="160" height="200">' : '<div class="fss-wishlist-card__img-placeholder"></div>')
      + '</a><div class="fss-wishlist-card__info">'
      + '<a href="' + url + '" class="fss-wishlist-card__title">' + p.title + '</a>'
      + '<div class="fss-wishlist-card__price">'
      + '<span class="fss-wishlist-card__price-current">' + price + '</span>'
      + (compare ? '<span class="fss-wishlist-card__price-compare">' + compare + '</span>' : '')
      + '</div><div class="fss-wishlist-card__actions">'
      + (available && variantId
        ? '<button class="fss-wishlist-card__atc" data-wishlist-atc="' + variantId + '">Add to bag</button>'
        : '<span class="fss-wishlist-card__sold-out">Sold out</span>')
      + '<button class="fss-wishlist-card__remove" data-wishlist-remove="' + p.id + '" aria-label="Remove">'
      + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      + '</button></div></div></div>';
  }
}

if (!customElements.get('fss-wishlist-drawer')) {
  customElements.define('fss-wishlist-drawer', FSSWishlistDrawer);
}

// -- Toast --

const FSSWishlistToast = {
  el: null, timer: null,
  show(msg) {
    if (!this.el) {
      this.el = document.createElement('div');
      this.el.className = 'fss-wishlist-toast';
      document.body.appendChild(this.el);
    }
    this.el.textContent = msg;
    this.el.classList.add('fss-wishlist-toast--visible');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.el.classList.remove('fss-wishlist-toast--visible'), 2200);
  }
};

// -- Global delegation --

document.addEventListener('click', (e) => {
  const btn = e.target.closest('fss-wishlist-button');
  if (btn) btn.handleClick(e);
  const trigger = e.target.closest('[data-wishlist-open]');
  if (trigger) document.querySelector('fss-wishlist-drawer')?.open();
});
